import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Divider,
  Button,
  TextField,
  InputAdornment,
  Drawer,
  Container,
} from "@mui/material";
import { ExpandLess, ExpandMore, Search, Clear, FilterList } from "@mui/icons-material";
import { allProducts, ProductInfo } from "./AllProducts";
import { SERVER_BASE_URL } from "./Config";
import { Link } from "react-router-dom";

interface DataStructure {
  [category: string]: string[] | { [subCategory: string]: string[] };
}

interface CategoryProps {
  category: string;
  subcategories: string[];
  selected: string[];
  setSelected: (subcategories: string[]) => void;
}

const Category: React.FC<CategoryProps> = ({
  category,
  subcategories,
  selected,
  setSelected,
}) => {
  const [open, setOpen] = useState(false);

  const handleToggleOpen = () => setOpen(!open);

  const handleToggleAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelected(event.target.checked ? subcategories : []);
  };

  const handleToggleSubcategory = (name: string) => {
    setSelected(
      selected.includes(name)
        ? selected.filter((item) => item !== name)
        : [...selected, name],
    );
  };

  return (
    <div>
      <ListItem disableGutters sx={{ padding: "4px 0" }}>
        <Checkbox
          checked={
            selected.length === subcategories.length && subcategories.length > 0
          }
          indeterminate={
            selected.length > 0 && selected.length < subcategories.length
          }
          onChange={handleToggleAll}
        />
        <ListItemText
          primary={
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", cursor: "pointer" }}
              onClick={handleToggleOpen}
            >
              {category}
            </Typography>
          }
        />
        <IconButton onClick={handleToggleOpen} size="small">
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </ListItem>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 3 }}>
          {subcategories.map((subcategory) => (
            <ListItem
              key={subcategory}
              disableGutters
              button
              onClick={() => handleToggleSubcategory(subcategory)}
              sx={{ padding: "2px 0" }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox
                  edge="start"
                  checked={selected.includes(subcategory)}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={subcategory} />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </div>
  );
};

const Sidebar: React.FC<{
  setSelectedCategories: (categories: { [key: string]: string[] }) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  products: ProductInfo[];
  categories: DataStructure;
}> = ({ setSelectedCategories, searchTerm, setSearchTerm, products, categories }) => {
  const [selectedCategories, setSelectedCategoriesState] = useState<{
    [key: string]: string[];
  }>({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Use categories directly from database - they're already filtered by available products
  const getFilteredData = () => {
    return categories;
  };

  const filteredData = getFilteredData();

  const handleCategoryChange = (category: string, newSelection: string[]) => {
    const updatedSelection = {
      ...selectedCategories,
      [category]: newSelection,
    };
    setSelectedCategoriesState(updatedSelection);
    setSelectedCategories(updatedSelection);
  };

  const handleClearFilters = () => {
    setSelectedCategoriesState({});
    setSelectedCategories({});
  };

  return (
    <>
      <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "flex-end", mt: -1, mb: 0.5 }}>
        <Button
          variant="outlined"
          onClick={() => setIsDrawerOpen(true)}
          startIcon={<FilterList />}
          sx={{
            borderRadius: 6,
            textTransform: "none",
            fontWeight: 500,
            px: 2.5,
            borderColor: "rgba(0, 0, 0, 0.23)",
            color: "text.primary",
          }}
        >
          Filters
        </Button>
      </Box>

      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: "80%", padding: 2 },
        }}
      >
        <Typography variant="h6" mb={2}>
          Filter
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <SidebarContent
          selectedCategories={selectedCategories}
          handleCategoryChange={handleCategoryChange}
          handleClearFilters={handleClearFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filteredData={filteredData}
          products={products}
        />
      </Drawer>

      <Box
        sx={{
          width: { xs: "100%", md: "250px" },
          maxWidth: 250,
          padding: 2,
          borderRight: "1px solid #ddd",
          height: "100vh",
          overflowY: "auto",
          display: { xs: "none", md: "block" },
        }}
      >
        <SidebarContent
          selectedCategories={selectedCategories}
          handleCategoryChange={handleCategoryChange}
          handleClearFilters={handleClearFilters}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filteredData={filteredData}
          products={products}
        />
      </Box>
    </>
  );
};

const SidebarContent: React.FC<{
  selectedCategories: { [key: string]: string[] };
  handleCategoryChange: (category: string, newSelection: string[]) => void;
  handleClearFilters: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filteredData: DataStructure;
  products: ProductInfo[];
}> = ({ selectedCategories, handleCategoryChange, handleClearFilters, searchTerm, setSearchTerm, filteredData, products }) => (
  <>
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Typography variant="body1">
        {Object.values(selectedCategories).flat().length > 0
          ? Object.values(selectedCategories).flat().length
          : products.length}{" "}
        Results
      </Typography>
      <Button
        variant="text"
        size="small"
        color="primary"
        startIcon={<Clear />}
        onClick={handleClearFilters}
      >
        Clear Filters
      </Button>
    </Box>

    <TextField
      fullWidth
      placeholder="Search Products"
      variant="outlined"
      size="small"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        ),
      }}
      sx={{ mb: 2 }}
    />

    {Object.keys(filteredData).map((category) => (
      <Category
        key={category}
        category={category}
        subcategories={
          Array.isArray(filteredData[category])
            ? (filteredData[category] as string[])
            : Object.keys(filteredData[category] as object)
        }
        selected={selectedCategories[category] || []}
        setSelected={(newSelection) =>
          handleCategoryChange(category, newSelection)
        }
      />
    ))}
  </>
);

const ListProducts: React.FC = () => {
  const [products, setProducts] = useState<ProductInfo[]>(allProducts);
  const [categories, setCategories] = useState<DataStructure>({});
  const [selectedCategories, setSelectedCategories] = useState<{
    [key: string]: string[];
  }>({});
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Helper function to find the category for a product type
  const findCategoryForType = (productType: string): string | null => {
    for (const [category, subcategories] of Object.entries(categories)) {
      const subcategoryList = Array.isArray(subcategories)
        ? subcategories
        : Object.keys(subcategories);

      // Check if productType is in subcategories OR matches the category name itself
      if (subcategoryList.includes(productType) || category === productType) {
        return category;
      }
    }
    return null;
  };

  const loadCategories = async () => {
    try {
      const providerToken = localStorage.getItem("providerToken");
      const response = await fetch(`${SERVER_BASE_URL}/categories`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${providerToken}`,
        },
      });
      const data = await response.json();
      setCategories(data.categories || {});
    } catch (error) {
      console.error("Failed to load categories:", error);
      // Fallback to empty categories if API fails
      setCategories({});
    }
  };

  const loadProducts = async () => {
    const providerToken = localStorage.getItem("providerToken");
    const response = await fetch(`${SERVER_BASE_URL}/products`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${providerToken}`,
      },
    });
    const data = await response.json();
    setProducts(data["products"]);
  };

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  // Define category and dosage form sort orders
  const categoryOrder = [
    "Weight Management",
    "Dermatology",
    "IV Therapy",
    "Mens Health",
    "Women's Health",
    "Anti-Aging",
    "Other"
  ];

  const dosageFormOrder = [
    "Syringe",
    "Injectable",
    "Oral",
    "Vial",
    "Topiclick",
    "Cream",
    "Capsules",
    "Suppository",
    "Pump"
  ];

  const filteredProducts = products.filter((product) => {
    const selectedSubcategories = Object.values(selectedCategories).flat();
    const matchesCategory = selectedSubcategories.length === 0 ||
      selectedSubcategories.includes(product.type) ||
      selectedSubcategories.includes(product.type2);
    const matchesSearch = searchTerm === "" ||
      product.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  }).sort((a, b) => {
    // Primary sort: by category
    const aCategoryIndex = categoryOrder.indexOf(a.type);
    const bCategoryIndex = categoryOrder.indexOf(b.type);
    const aCategorySort = aCategoryIndex === -1 ? 999 : aCategoryIndex;
    const bCategorySort = bCategoryIndex === -1 ? 999 : bCategoryIndex;

    if (aCategorySort !== bCategorySort) {
      return aCategorySort - bCategorySort;
    }

    // Secondary sort: by dosage form
    const aDosageIndex = dosageFormOrder.findIndex(form =>
      a.dosageForm?.toLowerCase().includes(form.toLowerCase())
    );
    const bDosageIndex = dosageFormOrder.findIndex(form =>
      b.dosageForm?.toLowerCase().includes(form.toLowerCase())
    );
    const aDosageSort = aDosageIndex === -1 ? 999 : aDosageIndex;
    const bDosageSort = bDosageIndex === -1 ? 999 : bDosageIndex;

    if (aDosageSort !== bDosageSort) {
      return aDosageSort - bDosageSort;
    }

    // Tertiary sort: by name
    return a.name.localeCompare(b.name);
  });

  return (
    <Container maxWidth="lg" sx={{ padding: { xs: 2, md: 3 } }}>
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md="auto">
          <Sidebar 
            setSelectedCategories={setSelectedCategories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            products={products}
            categories={categories}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md
          sx={{
            maxWidth: "900px", // Limit the max width for better centering
          }}
        >
          <Grid
            container
            spacing={3}
            justifyContent="flex-start"
            alignItems="stretch"
          >
            {filteredProducts.map((product) => (
              <Grid item key={product.id} xs={12} sm={6} md={4}>
                <Link
                  to={`/product/${product.id}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <Box
                    sx={{
                      padding: 2,
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center", // Centers the image vertically and horizontally
                      backgroundColor: "#fafafa",
                      width: "100%",
                      minHeight: "320px",
                      position: "relative",
                      textAlign: "left", // Ensures text is left-aligned
                    }}
                  >
                    <Box
                      component="img"
                      src={product.imageUrl}
                      alt={product.name}
                      sx={{
                        height: 200, // Make the image slightly larger
                        width: "auto",
                        maxWidth: "100%",
                        objectFit: "contain",
                        marginBottom: "auto",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 16,
                        left: 16,
                        width: "calc(100% - 32px)",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ marginBottom: 0.2 }}
                      >
                        {" "}
                        {/* Bold title with tighter spacing */}
                        {product.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ lineHeight: 1 }}
                      >
                        {" "}
                        {/* Smaller subtitle, reduced line height */}
                        {(() => {
                          const category = findCategoryForType(product.type);
                          const subcategory = product.type2 || product.type;
                          return category
                            ? `${category} > ${subcategory}`
                            : `Type: ${product.type}`;
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                </Link>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ListProducts;
